'use client';
import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye, EyeOff, Shield, CheckCircle, Users, Gamepad2,
  Copy, ArrowRight, Star, X
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import {
  trackAuthModeSwitch,
  trackLoginAttempt,
  trackLoginSuccess,
  trackLoginFailure,
  trackSignUpAttempt,
  trackSignUpSuccess,
  trackSignUpFailure,
  trackForgotPasswordRequest,
} from '@/lib/analytics';

type LoginFormData = {
  email: string;
  password: string;
  remember: boolean;
};

type SignupFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  accountType: 'parent' | 'player';
  agreeTerms: boolean;
};

const demoCredentials = [
  { role: 'Parent', email: 'maria@safeplay.gg', password: 'Family2026!' },
  { role: 'Player', email: 'alex@safeplay.gg', password: 'Player2026!' },
  { role: 'Admin', email: 'admin@safeplay.gg', password: 'Admin2026!' },
];

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const supabase = createClient();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { firstName: '', lastName: '', email: '', password: '', confirmPassword: '', accountType: 'parent', agreeTerms: false },
  });

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    trackLoginAttempt('email');
    try {
      await signIn(data.email, data.password);
      trackLoginSuccess('unknown');
      toast.success('Welcome back! Redirecting…');
      router.push('/platform-dashboard');
      router.refresh();
    } catch (err: any) {
      const msg: string = err?.message || '';
      const isRateLimit =
        msg.toLowerCase().includes('rate limit') ||
        msg.toLowerCase().includes('too many') ||
        err?.status === 429;

      const reason = isRateLimit ? 'rate_limit' : 'invalid_credentials';
      trackLoginFailure(reason);

      loginForm.setError('email', {
        message: isRateLimit
          ? 'Too many login attempts — please wait 60 seconds and try again.' : msg ||'Invalid credentials. Try the demo accounts below.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    trackSignUpAttempt(data.accountType);
    try {
      await signUp(data.email, data.password, {
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: `${data.firstName} ${data.lastName}`.trim(),
        role: data.accountType,
      });
      // Send welcome email (fire-and-forget)
      const { sendWelcomeEmail } = await import('@/lib/email/sendEmail');
      sendWelcomeEmail(data.email, data.firstName);
      trackSignUpSuccess(data.accountType);
      toast.success('Account created! Please verify your email to continue.');
      router.push('/verify-email');
      router.refresh();
    } catch (err: any) {
      trackSignUpFailure(err?.message || 'unknown_error');
      signupForm.setError('email', {
        message: err?.message || 'Could not create account. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    trackForgotPasswordRequest();
    try {
      const redirectTo = `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback?type=recovery`;
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo,
      });
      if (error) throw error;

      // Fire Resend password reset email (fire-and-forget)
      try {
        const { sendPasswordResetEmail } = await import('@/lib/email/sendEmail');
        const name = forgotEmail.split('@')[0];
        sendPasswordResetEmail(forgotEmail, name, redirectTo);
      } catch {
        // non-critical — Supabase already sent the reset link
      }

      setForgotSent(true);
    } catch (err: any) {
      toast.error(err?.message || 'Could not send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const autofillCredential = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen flex">
      <Toaster position="bottom-right" richColors />

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative">
            <button
              onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {forgotSent ? (
              <div className="text-center">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={28} className="text-teal-600" />
                </div>
                <h2 className="font-display text-xl font-bold text-slate-900 mb-2">Check your inbox</h2>
                <p className="text-sm text-slate-500 mb-6">
                  We sent a password reset link to <strong>{forgotEmail}</strong>. Check your email and follow the instructions.
                </p>
                <button
                  onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-xl text-sm transition-all"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="font-display text-xl font-bold text-slate-900 mb-1">Reset your password</h2>
                  <p className="text-sm text-slate-500">Enter your email and we'll send you a reset link.</p>
                </div>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Email address
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="w-full px-3.5 py-2.5 text-sm border border-slate-200 bg-slate-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
                      placeholder="you@email.com"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold py-3 rounded-xl text-sm transition-all"
                  >
                    {forgotLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Send Reset Link <ArrowRight size={16} /></>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700 p-10 relative overflow-hidden">
        {/* Decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
              backgroundSize: '28px 28px'
            }}
          />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-2">
          <AppLogo size={40} />
          <span className="font-display font-bold text-2xl text-white tracking-tight">SafePlay</span>
        </div>

        {/* Center content */}
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 mb-6">
            <Shield size={13} className="text-amber-400" />
            <span className="text-xs font-medium text-white/90">ID-verified gaming for families</span>
          </div>
          <h2 className="font-display text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
            Play together.<br />
            <span className="text-amber-400">Stay safe.</span>
          </h2>
          <p className="text-teal-200 text-base leading-relaxed max-w-sm mb-8">
            Every player on SafePlay has verified their identity — so you always know who your family is gaming with.
          </p>

          {/* Feature list */}
          <div className="space-y-3">
            {[
              { icon: Shield, text: 'Government ID verification on every account' },
              { icon: Users, text: 'Family dashboard with real-time player status' },
              { icon: Gamepad2, text: 'Verified access across 40+ gaming platforms' },
            ].map((item) => {
              const ItemIcon = item.icon;
              return (
                <div key={`feature-${item.text}`} className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ItemIcon size={14} className="text-amber-400" />
                  </div>
                  <span className="text-sm text-teal-100">{item.text}</span>
                </div>
              );
            })}
          </div>

          {/* Stars */}
          <div className="mt-8 flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map((i) => (
                <Star key={`auth-star-${i}`} size={14} className="text-amber-400 fill-amber-400" />
              ))}
            </div>
            <span className="text-xs text-teal-300">4.9/5 · 2,300 parent reviews</span>
          </div>
        </div>

        {/* Promo bottom */}
        <div className="relative bg-amber-500/20 border border-amber-400/30 rounded-xl px-4 py-3">
          <p className="text-sm font-semibold text-amber-300">🎁 First month free for new families</p>
          <p className="text-xs text-amber-200/80 mt-0.5">Use code SAFEPLAY1FREE at checkout · No card charged during trial</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-12 bg-white overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-display font-bold text-xl text-teal-700">SafePlay</span>
          </div>

          {/* Mode tabs */}
          <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`tab-${m}`}
                onClick={() => { setMode(m); trackAuthModeSwitch(m); }}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === m ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {mode === 'login' ? (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5" noValidate>
              <div>
                <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Welcome back</h1>
                <p className="text-sm text-slate-500">Sign in to your SafePlay family account</p>
              </div>

              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${loginForm.formState.errors.email ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                  placeholder="maria@safeplay.gg"
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-red-600">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="login-password" className="block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-teal-600 hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required' })}
                    className={`w-full px-3.5 py-2.5 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${loginForm.formState.errors.password ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-red-600">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="remember"
                  type="checkbox"
                  {...loginForm.register('remember')}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <label htmlFor="remember" className="text-sm text-slate-600">Remember me for 30 days</label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold py-3 rounded-xl text-sm transition-all duration-150 active:scale-95"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight size={16} /></>
                )}
              </button>

              {/* Demo credentials */}
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                <p className="text-xs font-semibold text-slate-600 mb-3">Demo accounts — click to autofill</p>
                <div className="space-y-2">
                  {demoCredentials.map((cred) => (
                    <div key={`cred-${cred.role}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded mr-2">{cred.role}</span>
                        <span className="text-xs text-slate-600 font-mono">{cred.email}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => autofillCredential(cred)}
                        className="text-xs font-semibold text-teal-600 hover:text-teal-800 px-2 py-1 bg-teal-50 rounded transition-colors"
                      >
                        Use
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(cred.email)}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Copy email"
                      >
                        <Copy size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          ) : (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4" noValidate>
              <div>
                <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Create your account</h1>
                <p className="text-sm text-slate-500">Start your free month — no card charged today</p>
              </div>

              {/* Account type toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Account type</label>
                <div className="flex gap-3">
                  {([
                    { value: 'parent', label: 'Parent / Guardian', icon: Users },
                    { value: 'player', label: 'Player (13+)', icon: Gamepad2 },
                  ] as const).map((type) => {
                    const TypeIcon = type.icon;
                    const selected = signupForm.watch('accountType') === type.value;
                    return (
                      <button
                        key={`type-${type.value}`}
                        type="button"
                        onClick={() => signupForm.setValue('accountType', type.value)}
                        className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all duration-150 ${
                          selected ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <TypeIcon size={16} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-1.5">Players under 13 must use a parent account</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-1.5">First name</label>
                  <input
                    id="firstName"
                    type="text"
                    {...signupForm.register('firstName', { required: 'Required' })}
                    className={`w-full px-3.5 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${signupForm.formState.errors.firstName ? 'border-red-400' : 'border-slate-200 bg-slate-50'}`}
                    placeholder="Maria"
                  />
                  {signupForm.formState.errors.firstName && (
                    <p className="mt-1 text-xs text-red-600">{signupForm.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-1.5">Last name</label>
                  <input
                    id="lastName"
                    type="text"
                    {...signupForm.register('lastName', { required: 'Required' })}
                    className={`w-full px-3.5 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${signupForm.formState.errors.lastName ? 'border-red-400' : 'border-slate-200 bg-slate-50'}`}
                    placeholder="Rodriguez"
                  />
                  {signupForm.formState.errors.lastName && (
                    <p className="mt-1 text-xs text-red-600">{signupForm.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                <input
                  id="signup-email"
                  type="email"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${signupForm.formState.errors.email ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                  placeholder="you@email.com"
                />
                {signupForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-red-600">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <p className="text-xs text-slate-400 mb-1.5">At least 8 characters, one uppercase, one number</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                      pattern: { value: /(?=.*[A-Z])(?=.*\d)/, message: 'Must include uppercase and number' },
                    })}
                    className={`w-full px-3.5 py-2.5 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${signupForm.formState.errors.password ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-red-600">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    className={`w-full px-3.5 py-2.5 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all ${signupForm.formState.errors.confirmPassword ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle confirm password">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="agreeTerms"
                  type="checkbox"
                  {...signupForm.register('agreeTerms', { required: 'You must agree to continue' })}
                  className="w-4 h-4 mt-0.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
                />
                <label htmlFor="agreeTerms" className="text-xs text-slate-600 leading-relaxed">
                  I agree to the{' '}
                  <a href="#" className="text-teal-600 hover:underline font-medium">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-teal-600 hover:underline font-medium">Privacy Policy</a>.
                  I confirm I am 18+ or a parent/guardian.
                </label>
              </div>
              {signupForm.formState.errors.agreeTerms && (
                <p className="text-xs text-red-600">{signupForm.formState.errors.agreeTerms.message}</p>
              )}

              {/* Promo reminder */}
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <CheckCircle size={14} className="text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-800 font-medium">Your first month is free — apply code <span className="font-mono">SAFEPLAY1FREE</span> at checkout</p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold py-3 rounded-xl text-sm transition-all duration-150 active:scale-95"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Create Account — First Month Free <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          <p className="text-center text-xs text-slate-400 mt-6">
            {mode === 'login' ? (
              <>No account yet?{' '}
                <button onClick={() => setMode('signup')} className="text-teal-600 font-semibold hover:underline">Create one free</button>
              </>
            ) : (
              <>Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-teal-600 font-semibold hover:underline">Sign in</button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}